import { Refresh } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { FormControlLabel, Grid, Radio, RadioGroup, Tooltip } from "@mui/material";
import Cross from "assets/img/svg/cross";
import classNames from "classnames";
import type { TEditingBeneficiarProfile } from "components/BPSettings/types";
import LoadingDots from "components/LoadingDots";
import LabelComponent from "components/shared/LabelComponent";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import RenderIf from "helpers/RenderIf";
import { SmallBody } from "helpers/TypographyComponents";
import { getJsonOrFalse, isObject, isPositiveInteger } from "helpers/utils";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ICustomField, StoreCustomField } from "store/customFieldsStore";
import { uploadFile } from "store/documentStore";
import { useAppDispatch } from "store/store";

import styles from "./style.module.scss";

interface IBeneficiaryCustomFields {
    /**
     * Data of the field being displayed.
     */
    field: StoreCustomField | undefined;
    /**
     * Full data of field being displayed (as in customField store).
     */
    fullFieldData: ICustomField;
    /**
     * Modifies editingProfile whenever there is a change that is made to reflect it visually and value is ready in case changes are saved.
     * @param ev Data of event triggered.
     * @param data Data of the specific page (so editingProfile.account for instance).
     * @param customFieldsInfo Data needed to update + format CF: data = fullCustomFieldsData, field = field saved in Benef info, fullFieldData = data of CF as saved in store.
     */
    handleChange: (
        ev: any,
        data: any,
        customFieldsInfo?: { data: any; field: any; fullFieldData?: ICustomField },
    ) => void;
    /**
     * Data of the specific page (so editingProfile.account for instance).
     */
    data: TEditingBeneficiarProfile["account"] | TEditingBeneficiarProfile["tax"] | undefined;
    /**
     * Handles saving fileData into editingProfile value:
     */
    handleFileSaving: (data: any, fileData: string, field: any, fullFieldData: ICustomField) => void;
    /**
     * Custom fields data saved in Benef profile:
     */
    customFieldsData: StoreCustomField[] | undefined;
    /**
     * Render required field props
     */
    requiredFieldProps?: (fieldName: string) =>
        | {
              error: boolean;
              helperText: string;
          }
        | undefined;
    /**
     * on blur field set error
     */
    handleFieldError?: (e: any, fieldName: string) => void;
}

// Component that displays company custom field on each Settings page depending on its valueType.
const BeneficiaryCustomFields = ({
    field,
    fullFieldData,
    handleChange,
    data,
    handleFileSaving,
    customFieldsData,
    requiredFieldProps,
    handleFieldError,
}: IBeneficiaryCustomFields) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [fileName, setFileName] = useState("");
    const [fileURL, setFileURL] = useState(""); // Currently this isn't being used, but just in case, the logic of saving URL is already implemented.
    const [statusUpload, setStatusUpload] = useState("");

    /**
     * Sends data of file to backend to get a link that can be saved into Beneficiary info.
     * @param files Data of file that was uploaded.
     */
    const handleFileUpload = (files: any) => {
        const file = files[0];
        const fileName = file.name ? file.name : "";
        const formData = new FormData();
        formData.append("file", file, fileName);

        dispatch(uploadFile({ formData })).then((resp) => {
            if (resp.meta.requestStatus === "fulfilled") {
                setFileName(fileName);
                resp.payload && setFileURL(resp?.payload.imagePath as string);
                const fileData = { fileName: fileName, link: resp.payload.imagePath };
                handleFileSaving(customFieldsData, JSON.stringify(fileData), field, fullFieldData);
                setStatusUpload("fulfilled");
            }
        });
    };

    /**
     * Handles removing the file that was uploaded > saving an empty string to form.
     */
    const handleRemovingFile = () => {
        handleFileSaving(customFieldsData, "", field, fullFieldData);
        setFileName("");
        setFileURL("");
    };

    // If file already saved, return parsed value, if not it is set to an empty string.
    const savedFile = field?.value ? getJsonOrFalse(field.value) : "";

    // Needed for displaying name of file when editing form:
    useEffect(() => {
        if (savedFile && isObject(savedFile)) {
            setFileName(savedFile.fileName);
            setFileURL(savedFile.link);
        } else {
            setFileName("");
            setFileURL("");
        }
    }, [savedFile]);

    const booleanOptions = [
        { label: t("CustomFields.CollapseInfo.Yes") },
        { label: t("CustomFields.CollapseInfo.No") },
    ];

    /**
     * (boolean) Is field not longstring or a fileimport (which need specific rendering).
     */
    const isFieldSimple = fullFieldData && !["longstring", "fileimport", "boolean"].includes(fullFieldData.valueType);

    const handleValidateInput = (ev, data, customFieldData) => {
        if (customFieldData.fullFieldData.valueType === "number") {
            if (!isPositiveInteger(ev.target.value)) return;
        }
        handleChange(ev, data, customFieldData);
    };

    if (fullFieldData && field && isFieldSimple) {
        const fieldError = requiredFieldProps && requiredFieldProps!(String(fullFieldData.id));
        return (
            <Grid item xs={12} md={12}>
                <Tooltip title={fullFieldData?.name.length > 30 ? fullFieldData?.name : ""}>
                    <LabelComponent
                        htmlFor={fullFieldData.name ?? `field.${field.customFieldTypeId}`}
                        className={classNames({ "field-required": fullFieldData.isRequired })}
                    >
                        {fullFieldData.name ?? ""}
                    </LabelComponent>
                </Tooltip>
                <StyledTextField
                    {...fieldError}
                    value={field ? field.value : ""}
                    name={`customFields.${field.customFieldTypeId}`}
                    id={fullFieldData.name}
                    onBlur={(e) => handleFieldError && handleFieldError(e, String(fullFieldData.id))}
                    fullWidth
                    onChange={(ev) => handleValidateInput(ev, data, { data: customFieldsData, field, fullFieldData })}
                />
            </Grid>
        );
    } else if (fullFieldData && field && fullFieldData.valueType === "boolean") {
        const fieldError = requiredFieldProps && requiredFieldProps!(String(fullFieldData.id));
        return (
            <Grid item xs={12} md={12}>
                <Tooltip title={fullFieldData?.name.length > 30 ? fullFieldData?.name : ""}>
                    <LabelComponent
                        htmlFor={fullFieldData.name ?? `field.${field.customFieldTypeId}`}
                        className={classNames({ "field-required": fullFieldData.isRequired })}
                    >
                        {fullFieldData.name ?? ""}
                    </LabelComponent>
                </Tooltip>
                <RadioGroup
                    {...fieldError}
                    id={fullFieldData.name}
                    name={`customFields.${field.customFieldTypeId}`}
                    row
                    value={field ? field.value : ""}
                    sx={{ paddingLeft: "4px" }}
                    onChange={(ev) => handleValidateInput(ev, data, { data: customFieldsData, field, fullFieldData })}
                >
                    {booleanOptions &&
                        booleanOptions.map((opt, index) => (
                            <FormControlLabel
                                id={`${field.customFieldTypeId}-${index}`}
                                value={`${index}`}
                                control={<Radio checkedIcon={<CheckCircleIcon />} />}
                                label={opt.label}
                                key={`option-${index}`}
                            />
                        ))}
                </RadioGroup>
            </Grid>
        );
    } else if (fullFieldData && field && fullFieldData.valueType === "fileimport") {
        const fieldError = requiredFieldProps && requiredFieldProps!(String(fullFieldData.id));
        return (
            <Grid item xs={12} md={12}>
                <Tooltip title={fullFieldData?.name.length > 30 ? fullFieldData?.name : ""}>
                    <LabelComponent
                        htmlFor={fullFieldData.name ?? `field.${field.customFieldTypeId}`}
                        className={classNames({ "field-required": fullFieldData.isRequired })}
                    >
                        {fullFieldData.name ?? ""}
                    </LabelComponent>
                </Tooltip>
                <div
                    className={classNames({
                        [styles["upload-file-btn"]]: true,
                        "upload-file-error": fieldError?.error,
                    })}
                >
                    {statusUpload === "pending" ? (
                        <LoadingDots />
                    ) : (
                        <>
                            {!fileName && (
                                <StyledButton
                                    onClick={() => fileInputRef.current?.click()}
                                    className={styles["button"]}
                                    buttonType="outlined"
                                >
                                    {t("CustomFields.ChooseFile")}
                                </StyledButton>
                            )}
                            <span className={styles["text"]}>
                                {fileName ? fileName : t("CustomFields.NoFileChosen")}
                            </span>
                            {fileName && (
                                <>
                                    <Refresh
                                        className={styles["resfresh-icon"]}
                                        onClick={() => fileInputRef.current?.click()}
                                    />
                                    <Cross
                                        style={{ width: "15px", height: "15px", cursor: "pointer" }}
                                        onClick={() => {
                                            handleRemovingFile();
                                        }}
                                    />
                                </>
                            )}
                        </>
                    )}
                </div>
                <input
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={(event) => {
                        setStatusUpload("pending");
                        handleFileUpload(event.target.files);
                    }}
                    accept=".pdf, .png, .jpg, .jpeg"
                    id="upload-file"
                    type="file"
                />
                <RenderIf show={!!fieldError}>
                    <div className={styles["upload-file-helper-text"]}>
                        <SmallBody color="danger">{fieldError?.helperText}</SmallBody>
                    </div>
                </RenderIf>
            </Grid>
        );
    } else if (fullFieldData && field && fullFieldData.valueType === "longstring") {
        const fieldError = requiredFieldProps && requiredFieldProps!(String(fullFieldData.id));
        return (
            <Grid item xs={12} md={12}>
                <Tooltip title={fullFieldData?.name.length > 30 ? fullFieldData?.name : ""}>
                    <LabelComponent
                        htmlFor={fullFieldData.name ?? `field.${field.customFieldTypeId}`}
                        className={classNames({ "field-required": fullFieldData.isRequired })}
                    >
                        {fullFieldData.name ?? ""}
                    </LabelComponent>
                </Tooltip>
                <StyledTextField
                    {...fieldError}
                    value={field ? field.value : ""}
                    name={`customFields.${field.customFieldTypeId}`}
                    id={fullFieldData.name}
                    fullWidth
                    onBlur={(e) => handleFieldError && handleFieldError(e, String(fullFieldData.id))}
                    onChange={(ev) => handleValidateInput(ev, data, { data: customFieldsData, field, fullFieldData })}
                    multiline
                    maxRows={4}
                />
            </Grid>
        );
    }
};

export default BeneficiaryCustomFields;
